import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { AIGeneratedInsight } from '@coffeeplexity/shared';

interface AIInsightCardProps {
  insight: AIGeneratedInsight;
  onPress?: () => void;
}

export const AIInsightCard: React.FC<AIInsightCardProps> = ({
  insight,
  onPress,
}) => {
  const getInsightIcon = () => {
    switch (insight.type) {
      case 'achievement':
        return 'trophy';
      case 'improvement':
        return 'trending-up';
      case 'pattern':
        return 'analytics';
      case 'prediction':
        return 'telescope';
      case 'recommendation':
        return 'bulb';
      default:
        return 'information-circle';
    }
  };

  const getInsightColor = () => {
    switch (insight.type) {
      case 'achievement':
        return '#F59E0B';
      case 'improvement':
        return '#10B981';
      case 'pattern':
        return '#6366F1';
      case 'prediction':
        return '#8B5CF6';
      case 'recommendation':
        return '#06B6D4';
      default:
        return '#6B7280';
    }
  };

  const getPriorityColor = () => {
    switch (insight.priority) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const formatConfidence = (confidence: number) => {
    return `${Math.round(confidence * 100)}% confidence`;
  };

  const CardContent = (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <View
            style={[
              styles.iconBackground,
              { backgroundColor: `${getInsightColor()}20` },
            ]}
          >
            <Ionicons
              name={getInsightIcon() as any}
              size={20}
              color={getInsightColor()}
            />
          </View>
          <View style={styles.headerText}>
            <Text style={styles.title}>{insight.title}</Text>
            <View style={styles.metadata}>
              <View
                style={[
                  styles.priorityBadge,
                  { backgroundColor: getPriorityColor() },
                ]}
              >
                <Text style={styles.priorityText}>
                  {insight.priority.toUpperCase()}
                </Text>
              </View>
              <Text style={styles.confidence}>
                {formatConfidence(insight.confidence)}
              </Text>
            </View>
          </View>
        </View>

        {onPress && (
          <Ionicons name='chevron-forward' size={20} color='#9CA3AF' />
        )}
      </View>

      <Text style={styles.description}>{insight.description}</Text>

      {insight.actionable && insight.suggestedActions.length > 0 && (
        <View style={styles.actionsContainer}>
          <Text style={styles.actionsTitle}>Suggested Actions:</Text>
          {insight.suggestedActions.slice(0, 2).map((action, index) => (
            <View key={index} style={styles.actionItem}>
              <Ionicons
                name='checkmark-circle-outline'
                size={14}
                color='#10B981'
              />
              <Text style={styles.actionText}>{action}</Text>
            </View>
          ))}
          {insight.suggestedActions.length > 2 && (
            <Text style={styles.moreActions}>
              +{insight.suggestedActions.length - 2} more actions
            </Text>
          )}
        </View>
      )}

      <View style={styles.footer}>
        <Text style={styles.category}>{insight.category}</Text>
        <Text style={styles.timestamp}>
          {new Date(insight.generatedAt).toLocaleDateString()}
        </Text>
      </View>
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
        {CardContent}
      </TouchableOpacity>
    );
  }

  return CardContent;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  iconContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
  },
  iconBackground: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
    lineHeight: 20,
  },
  metadata: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  priorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  confidence: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  description: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 20,
    marginBottom: 12,
  },
  actionsContainer: {
    marginBottom: 12,
  },
  actionsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
    gap: 6,
  },
  actionText: {
    fontSize: 13,
    color: '#4B5563',
    flex: 1,
    lineHeight: 18,
  },
  moreActions: {
    fontSize: 12,
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: 4,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  category: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6750A4',
  },
  timestamp: {
    fontSize: 12,
    color: '#9CA3AF',
  },
});
